// MUI-style icon names for the older pages, drawn with Phosphor (one weight everywhere).
import { createElement, forwardRef } from 'react';
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  ArrowsLeftRight,
  Bank,
  BookmarksSimple,
  Calculator,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChartBar,
  ChartLine,
  ChartLineUp,
  ChartPieSlice,
  Check,
  CheckCircle,
  Checks,
  Circle,
  ClipboardText,
  ClockCounterClockwise,
  CloudArrowUp,
  Copy,
  CurrencyGbp,
  Diamond,
  DownloadSimple,
  File,
  FileArrowUp,
  FilePdf,
  FloppyDisk,
  Funnel,
  GoogleLogo,
  ImageBroken,
  Info,
  Lightning,
  List,
  MagnifyingGlass,
  Package,
  PencilSimple,
  Plus,
  Printer,
  PushPin,
  QrCode,
  Scan,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  ShoppingCartSimple,
  SignOut,
  SquaresFour,
  Star,
  Storefront,
  Tag,
  Ticket,
  Trash,
  TrendDown,
  TrendUp,
  Truck,
  UploadSimple,
  Warning,
  WarningCircle,
  X,
  XCircle,
} from '@phosphor-icons/react';
import { mergeStyles, resolveSx } from './theme';

const FONT_SIZES = { small: 16, medium: 20, large: 28, inherit: '1em' };

function makeIcon(Glyph, displayName, glyphProps = {}) {
  const Icon = forwardRef(function Icon({ size, fontSize, stroke, color = 'currentColor', style, sx, ...rest }, ref) {
    void stroke;
    const finalSize = size ?? FONT_SIZES[fontSize] ?? 20;
    return (
      <span
        ref={ref}
        style={mergeStyles({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'currentColor', flexShrink: 0 }, style, resolveSx(sx))}
      >
        {createElement(Glyph, { size: finalSize, color, weight: 'regular', 'aria-hidden': true, ...glyphProps, ...rest })}
      </span>
    );
  });
  Icon.displayName = displayName;
  return Icon;
}

export const Menu = makeIcon(List, 'MenuIcon');
export const Logout = makeIcon(SignOut, 'LogoutIcon');
export const Dashboard = makeIcon(SquaresFour, 'DashboardIcon');
export const CloudUpload = makeIcon(CloudArrowUp, 'CloudUploadIcon');
export const Upload = makeIcon(UploadSimple, 'UploadIcon');
export const BarChart = makeIcon(ChartBar, 'BarChartIcon');
export const Google = makeIcon(GoogleLogo, 'GoogleIcon');
export const ConfirmationNumber = makeIcon(Ticket, 'TicketIcon');
export const Inventory = makeIcon(Package, 'InventoryIcon');
export { DiamondIcon as Diamond };
const DiamondIcon = makeIcon(Diamond, 'DiamondIcon');
export const ShoppingCartCheckoutRounded = makeIcon(ShoppingCartSimple, 'CheckoutIcon');
export const AdminPanelSettings = makeIcon(ShieldCheck, 'AdminIcon');
export const ArrowBack = makeIcon(ArrowLeft, 'ArrowBackIcon');
export const ContentCopy = makeIcon(Copy, 'CopyIcon');
export const Download = makeIcon(DownloadSimple, 'DownloadIcon');
export const FileUpload = makeIcon(FileArrowUp, 'FileUploadIcon');
export { ShoppingBagIcon as ShoppingBag };
const ShoppingBagIcon = makeIcon(ShoppingBag, 'ShoppingBagIcon');
export const Insights = makeIcon(ChartLineUp, 'InsightsIcon');
export const SwapHoriz = makeIcon(ArrowsLeftRight, 'SwapIcon');
export const TrendingUp = makeIcon(TrendUp, 'TrendingUpIcon');
export const WarningAmber = makeIcon(Warning, 'WarningIcon');
export const Add = makeIcon(Plus, 'AddIcon');
export const Save = makeIcon(FloppyDisk, 'SaveIcon');
export const DeleteOutline = makeIcon(Trash, 'DeleteOutlineIcon');
export const Refresh = makeIcon(ArrowClockwise, 'RefreshIcon');
export const Search = makeIcon(MagnifyingGlass, 'SearchIcon');
export const FilterList = makeIcon(Funnel, 'FilterIcon');
export const Close = makeIcon(X, 'CloseIcon');
export { ShoppingCartIcon as ShoppingCart };
const ShoppingCartIcon = makeIcon(ShoppingCart, 'ShoppingCartIcon');
export const Delete = makeIcon(Trash, 'DeleteIcon');
export const ShowChart = makeIcon(ChartLine, 'ShowChartIcon');
export const TrendingDown = makeIcon(TrendDown, 'TrendingDownIcon');
export const TrendingFlat = makeIcon(ArrowRight, 'TrendingFlatIcon');
export const ViewModule = makeIcon(SquaresFour, 'ViewModuleIcon');
export const History = makeIcon(ClockCounterClockwise, 'HistoryIcon');
export { StarIcon as Star };
const StarIcon = makeIcon(Star, 'StarIcon');
export const SportsMartialArts = makeIcon(Lightning, 'SportsIcon');
export const ExpandMore = makeIcon(CaretDown, 'ExpandMoreIcon');
export const ExpandLess = makeIcon(CaretUp, 'ExpandLessIcon');
export const ArrowForward = makeIcon(ArrowRight, 'ArrowForwardIcon');
export const StarBorder = makeIcon(Star, 'StarBorderIcon');
export const StarRate = makeIcon(Star, 'StarRateIcon', { weight: 'fill' });
export const Loop = makeIcon(ArrowsClockwise, 'LoopIcon');
export const Assessment = makeIcon(ChartBar, 'AssessmentIcon');
export const LocalShipping = makeIcon(Truck, 'TruckIcon');
export const AccountBalanceRounded = makeIcon(Bank, 'BankIcon');
export const AddRounded = makeIcon(Plus, 'AddRoundedIcon');
export const ArrowBackRounded = makeIcon(ArrowLeft, 'ArrowBackRoundedIcon');
export const CheckRounded = makeIcon(Check, 'CheckIcon');
export const ChevronLeftRounded = makeIcon(CaretLeft, 'ChevronLeftIcon');
export const ChevronRightRounded = makeIcon(CaretRight, 'ChevronRightIcon');
export const CloseRounded = makeIcon(X, 'CloseRoundedIcon');
export const CollectionsBookmarkRounded = makeIcon(BookmarksSimple, 'CollectionsIcon');
export const DeleteOutlineRounded = makeIcon(Trash, 'DeleteRoundedIcon');
export const EditRounded = makeIcon(PencilSimple, 'EditRoundedIcon');
export const RefreshRounded = makeIcon(ArrowClockwise, 'RefreshRoundedIcon');
export const SearchRounded = makeIcon(MagnifyingGlass, 'SearchRoundedIcon');
export const ShowChartRounded = makeIcon(ChartLine, 'ShowChartRoundedIcon');
export const TrendingUpRounded = makeIcon(TrendUp, 'TrendingUpRoundedIcon');
export const BrokenImageRounded = makeIcon(ImageBroken, 'BrokenImageIcon');
export const CheckCircleRounded = makeIcon(CheckCircle, 'CheckCircleRoundedIcon');
export const DoneAllRounded = makeIcon(Checks, 'DoneAllRoundedIcon');
export const Inventory2Rounded = makeIcon(Package, 'Inventory2Icon');
export const LocalMallRounded = makeIcon(ShoppingBag, 'LocalMallIcon');
export const ArrowBackRoundedIcon = makeIcon(ArrowLeft, 'ArrowBackRoundedIconIcon');
export const AccountBalance = makeIcon(Bank, 'AccountBalanceIcon');
export const Analytics = makeIcon(ChartPieSlice, 'AnalyticsIcon');
export const AttachMoney = makeIcon(CurrencyGbp, 'MoneyIcon');
export const ArrowBackRoundedLarge = makeIcon(ArrowLeft, 'ArrowBackLargeIcon');
export { QrCodeIcon as QrCode };
const QrCodeIcon = makeIcon(QrCode, 'QrCodeIcon');
export const Print = makeIcon(Printer, 'PrintIcon');
export const Assignment = makeIcon(ClipboardText, 'AssignmentIcon');
export const Cancel = makeIcon(XCircle, 'CancelIcon');
export const Edit = makeIcon(PencilSimple, 'EditIcon');
export { PushPinIcon as PushPin };
const PushPinIcon = makeIcon(PushPin, 'PushPinIcon');
export const MenuIcon = makeIcon(List, 'MenuIconIcon');
export const UploadIcon = makeIcon(UploadSimple, 'UploadIconIcon');
export const SearchRoundedIcon = makeIcon(MagnifyingGlass, 'SearchRoundedIconIcon');
export const Calculate = makeIcon(Calculator, 'CalculateIcon');
export const Error = makeIcon(WarningCircle, 'ErrorIcon');
export const PictureAsPdf = makeIcon(FilePdf, 'PdfIcon');
export const BrokenImage = makeIcon(ImageBroken, 'BrokenImageIconIcon');
export const DoneAll = makeIcon(Checks, 'DoneAllIcon');
export const RefreshIcon = makeIcon(ArrowClockwise, 'RefreshIconIcon');
export const AddIcon = makeIcon(Plus, 'AddIconIcon');
export const SaveIcon = makeIcon(FloppyDisk, 'SaveIconIcon');
export const DeleteIcon = makeIcon(Trash, 'DeleteIconIcon');
export const Store = makeIcon(Storefront, 'StoreIcon');
export const RadioButtonUnchecked = makeIcon(Circle, 'RadioIcon');
export const QrCodeScanner = makeIcon(Scan, 'QrScannerIcon');
export const LocalOffer = makeIcon(Tag, 'LocalOfferIcon');
export const Label = makeIcon(Tag, 'LabelIcon');
export { InfoIcon as Info };
const InfoIcon = makeIcon(Info, 'InfoIcon');
export { CheckCircleIcon as CheckCircle };
const CheckCircleIcon = makeIcon(CheckCircle, 'CheckCircleIcon');

const defaultIcon = makeIcon(File, 'GenericDocIcon');
export default defaultIcon;
